<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '../auth';
import AppHeader from '@/components/layout/AppHeader.vue';
import { Button } from '@/components/ui/button';

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const clientName = ref('');
const scopes = ref<string[]>([]);
const loading = ref(true);
const submitting = ref(false);
const error = ref('');

// OAuth parameters from query string
const clientId = computed(() => route.query.client_id as string);
const redirectUri = computed(() => route.query.redirect_uri as string);
const responseType = computed(() => route.query.response_type as string);
const state = computed(() => route.query.state as string);
const codeChallenge = computed(() => route.query.code_challenge as string);
const codeChallengeMethod = computed(() => route.query.code_challenge_method as string);

const BACKEND_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Human-readable scope descriptions
const scopeDescriptions: Record<string, string> = {
  'flashcards:read': 'View your flashcards and decks',
  'flashcards:write': 'Create, edit, and delete flashcards and decks',
  'reviews:read': 'View your review history and statistics',
  'reviews:write': 'Submit flashcard reviews',
};

async function fetchClientInfo() {
  loading.value = true;
  error.value = '';

  try {
    // For now, we'll use the client_id to show basic info
    // In production, you might want to fetch client details from an endpoint
    clientName.value = clientId.value === 'claude-ai' ? 'Claude.ai' : clientId.value;

    // Always show the actual scopes that will be granted
    // The backend will grant all client scopes if the requested scope isn't recognized
    scopes.value = ['flashcards:read', 'flashcards:write', 'reviews:read', 'reviews:write'];
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load client information';
  } finally {
    loading.value = false;
  }
}

async function authorize() {
  submitting.value = true;
  error.value = '';

  try {
    const response = await fetch(`${BACKEND_URL}/oauth/authorize/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        client_id: clientId.value,
        redirect_uri: redirectUri.value,
        scope: scopes.value.join(' '),
        state: state.value || undefined,
        code_challenge: codeChallenge.value || undefined,
        code_challenge_method: codeChallengeMethod.value || undefined,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error_description || data.error || 'Authorization failed');
    }

    // Redirect to the client with the authorization code
    window.location.href = data.redirect_url;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Authorization failed';
    submitting.value = false;
  }
}

function deny() {
  // Redirect back to client with error
  const errorUrl = new URL(redirectUri.value);
  errorUrl.searchParams.set('error', 'access_denied');
  errorUrl.searchParams.set('error_description', 'User denied the authorization request');
  if (state.value) {
    errorUrl.searchParams.set('state', state.value);
  }
  window.location.href = errorUrl.toString();
}

onMounted(() => {
  // Check if we have required parameters
  if (!clientId.value || !redirectUri.value || !responseType.value) {
    error.value = 'Missing required OAuth parameters';
    loading.value = false;
    return;
  }

  if (!isLoading.value && !user.value) {
    // Redirect to sign-in with return URL
    const returnUrl = encodeURIComponent(window.location.href);
    router.push(`/sign-in?redirect=${returnUrl}`);
    return;
  }

  if (user.value) {
    fetchClientInfo();
  }
});

// Watch for session to finish loading
watch(user, (newUser) => {
  if (newUser && loading.value) {
    fetchClientInfo();
  } else if (!newUser && !isLoading.value) {
    // User logged out or session expired
    const returnUrl = encodeURIComponent(window.location.href);
    router.push(`/sign-in?redirect=${returnUrl}`);
  }
});
</script>

<template>
  <div class="min-h-screen bg-background">
    <AppHeader />

    <main class="max-w-md mx-auto px-4 py-8">
      <div
        v-if="isLoading || loading"
        class="text-center text-muted-foreground"
      >
        Loading...
      </div>

      <div
        v-else-if="error && !clientId"
        class="bg-destructive/10 text-destructive p-4 rounded-md"
      >
        {{ error }}
      </div>

      <div
        v-else-if="user"
        class="space-y-6"
      >
        <div class="text-center">
          <h2 class="text-2xl font-semibold">
            Authorize Application
          </h2>
          <p class="text-muted-foreground mt-2">
            <strong>{{ clientName }}</strong> wants to access your account
          </p>
        </div>

        <div
          v-if="error"
          class="bg-destructive/10 text-destructive p-4 rounded-md"
        >
          {{ error }}
        </div>

        <div class="bg-card rounded-lg shadow-md p-6 border border-border">
          <h3 class="font-medium mb-4">
            This application will be able to:
          </h3>
          <ul class="space-y-3">
            <li
              v-for="scopeItem in scopes"
              :key="scopeItem"
              class="flex items-start gap-3"
            >
              <span class="text-green-500 mt-0.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span>{{ scopeDescriptions[scopeItem] || scopeItem }}</span>
            </li>
          </ul>
        </div>

        <div class="bg-muted/50 rounded-lg p-4">
          <p class="text-sm text-muted-foreground">
            Authorizing will allow <strong>{{ clientName }}</strong> to access your LLM-SR account
            on your behalf. You can revoke access at any time.
          </p>
        </div>

        <div class="flex gap-3">
          <Button
            variant="outline"
            class="flex-1"
            :disabled="submitting"
            @click="deny"
          >
            Deny
          </Button>
          <Button
            class="flex-1"
            :disabled="submitting"
            @click="authorize"
          >
            {{ submitting ? 'Authorizing...' : 'Authorize' }}
          </Button>
        </div>

        <p class="text-xs text-center text-muted-foreground">
          Logged in as {{ user.email }}
        </p>
      </div>

      <div
        v-else
        class="text-center"
      >
        <p class="text-muted-foreground mb-4">
          You need to sign in to authorize this application.
        </p>
        <Button @click="router.push('/sign-in')">
          Sign In
        </Button>
      </div>
    </main>
  </div>
</template>
